import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { templatesApi } from '../../../utils/templatesApi';
import { buildKitPages, cleanContentForTemplate } from '../../../utils/templateContent';
import './TemplateGallery.css';

const CATEGORY_OPTIONS = [
    'Meetings and teamwork',
    'Planning and decisions',
    'Operations and support',
    'Personal and learning',
    'Other',
];

/**
 * "Save as template" (BBD-109), opened from the document viewers.
 *
 * Props:
 *   isOpen, onClose
 *   source: {
 *     name, description, content (raw editor HTML), docType: 'Space' | 'Page',
 *     hasChildren, loadDescendants: async () => [{title, data}]   // spaces with children
 *   }
 *   workspaceId, workspaceRole – offer "this workspace" scope to EDITOR and above
 *   onSaved(template)
 */
const SaveAsTemplateDialog = ({ isOpen, onClose, source, workspaceId = null, workspaceRole = null, onSaved }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('Other');
    const [includeChildren, setIncludeChildren] = useState(false);
    const [scope, setScope] = useState('personal');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const canUseWorkspace = Boolean(workspaceId && ['OWNER', 'ADMIN', 'EDITOR'].includes(workspaceRole));

    useEffect(() => {
        if (!isOpen) return;
        setName(source?.name ? `${source.name} template` : '');
        setDescription('');
        setCategory('Other');
        setIncludeChildren(false);
        setScope(canUseWorkspace ? 'workspace' : 'personal');
        setError('');
    }, [isOpen, source, canUseWorkspace]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { setError('Give the template a name.'); return; }
        setBusy(true);
        setError('');
        try {
            const kit = includeChildren && source.hasChildren && source.docType === 'Space';
            let pages = [];
            if (kit) {
                const descendants = await source.loadDescendants();
                pages = buildKitPages(descendants, source.name);
                if (!pages.length) {
                    setError('Could not read the child pages. Try again or save the space only.');
                    setBusy(false);
                    return;
                }
            }
            const payload = {
                name: name.trim(),
                description: description.trim(),
                category: category.trim(),
                icon: '',
                kind: kit ? 'kit' : (source.docType === 'Space' ? 'space' : 'page'),
                title_pattern: source.name || '',
                description_pattern: source.description || '',
                content: cleanContentForTemplate(source.content || ''),
                fields: [],
                pages,
            };
            if (scope === 'workspace' && canUseWorkspace) payload.workspace_id = workspaceId;
            const saved = await templatesApi.create(payload);
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            if (err.status === 413) setError(err.data?.message || 'Storage limit exceeded.');
            else setError(err.message || 'Could not save the template.');
        } finally {
            setBusy(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="TplGallery__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
            <form className="TplFields__modal" onSubmit={handleSubmit} role="dialog" aria-modal="true" aria-label="Save as template">
                <div className="TplGallery__header">
                    <h3 className="TplGallery__title">Save as template</h3>
                    <button type="button" className="TplGallery__close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="TplFields__body">
                    <div className="TplFields__field">
                        <label htmlFor="tpl-save-name">Name<span className="TplFields__required">*</span></label>
                        <input id="tpl-save-name" type="text" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                    </div>
                    <div className="TplFields__field">
                        <label htmlFor="tpl-save-desc">Use it when…</label>
                        <input id="tpl-save-desc" type="text" maxLength={500} value={description} placeholder="One line shown in the gallery" onChange={(e) => setDescription(e.target.value)} />
                    </div>
                    <div className="TplFields__field">
                        <label htmlFor="tpl-save-cat">Category</label>
                        <select id="tpl-save-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    {source?.docType === 'Space' && source?.hasChildren && (
                        <div className="TplFields__field">
                            <label>What to include</label>
                            <label className="TplFields__option">
                                <input type="radio" name="tpl-kind" checked={!includeChildren} onChange={() => setIncludeChildren(false)} />
                                Space only
                            </label>
                            <label className="TplFields__option">
                                <input type="radio" name="tpl-kind" checked={includeChildren} onChange={() => setIncludeChildren(true)} />
                                Space and its child pages, as a kit
                            </label>
                        </div>
                    )}
                    {canUseWorkspace && (
                        <div className="TplFields__field">
                            <label>Available to</label>
                            <label className="TplFields__option">
                                <input type="radio" name="tpl-scope" checked={scope === 'workspace'} onChange={() => setScope('workspace')} />
                                This workspace
                            </label>
                            <label className="TplFields__option">
                                <input type="radio" name="tpl-scope" checked={scope === 'personal'} onChange={() => setScope('personal')} />
                                Just me (personal)
                            </label>
                        </div>
                    )}
                    <div className="TplStart__note">
                        @mentions become plain text, #task references are kept, and attachments and comments are not copied.
                        You can add fill-in fields and variables afterwards under My templates.
                    </div>
                    {error && <div className="TplGallery__error" style={{ padding: 0 }}>{error}</div>}
                </div>
                <div className="TplGallery__previewFoot">
                    <button type="button" className="TplGallery__btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="submit" className="TplGallery__btn TplGallery__btn--primary" disabled={busy}>
                        {busy ? 'Saving…' : 'Save template'}
                    </button>
                </div>
            </form>
        </div>,
        document.body,
    );
};

export default SaveAsTemplateDialog;
